// ─── Template: lottie-asset-v1 ────────────────────────────────────────────
// Renderiza una animación Lottie elegida del buscador. Usa `@remotion/lottie`,
// que es frame-driven: la animación avanza con el playhead de Remotion en vez
// de tener su propio reloj. Esto resuelve dos cosas:
//   1) Cuando el video está pausado, la animación también queda quieta.
//   2) El preview es frame-accurate y se exporta correctamente.
//
// `@remotion/lottie` requiere el JSON crudo, no el `.lottie` binario. Por eso
// hacemos fetch del recurso y guardamos el resultado en estado. Mientras se
// está descargando mostramos un fallback discreto.

import React, { useEffect, useState } from 'react';
import { AbsoluteFill, delayRender, continueRender } from 'remotion';
import { Lottie, type LottieAnimationData } from '@remotion/lottie';
import type { AnimationTemplate } from '../types';

export type LottieAssetProps = {
  /** URL al `.json` de Lottie. La setea el picker cuando se inserta. */
  lottieUrl: string;
  /** Color de fondo de la composición (transparente por default). */
  background: string;
  /** Velocidad de reproducción (1 = normal). */
  speed: number;
};

const Component: React.FC<LottieAssetProps> = ({ lottieUrl, background, speed }) => {
  const [data, setData] = useState<LottieAnimationData | null>(null);
  // `delayRender` le dice al renderer (modo export) que espere a que el JSON
  // esté listo antes de pintar el frame. En el preview del player esto es
  // efectivamente un no-op, pero garantiza que el render server-side vea la
  // animación.
  const [handle] = useState(() =>
    lottieUrl ? delayRender('Cargando animación Lottie') : null,
  );

  useEffect(() => {
    if (!lottieUrl) return;
    let cancelled = false;
    fetch(lottieUrl)
      .then(res => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then((json: LottieAnimationData) => {
        if (cancelled) return;
        setData(json);
        if (handle != null) continueRender(handle);
      })
      .catch(() => {
        // Si falla, igual liberamos el render para no bloquear el export.
        if (handle != null) continueRender(handle);
      });
    return () => { cancelled = true; };
  }, [lottieUrl, handle]);

  return (
    <AbsoluteFill style={{
      background,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      {!lottieUrl ? (
        <div style={{ color: '#888', fontFamily: 'Inter, sans-serif', fontSize: 14 }}>
          Sin animación seleccionada
        </div>
      ) : data ? (
        <Lottie
          animationData={data}
          playbackRate={Math.max(0.1, speed || 1)}
          loop
          style={{ width: '80%', height: '80%' }}
        />
      ) : null}
    </AbsoluteFill>
  );
};

export const lottieAssetTemplate: AnimationTemplate<LottieAssetProps> = {
  id: 'lottie-asset-v1',
  label: 'Animación Lottie',
  description: 'Animación vectorial de la biblioteca Lottie.',
  category: 'lottie',
  defaultDurationSec: 4,
  defaultProps: {
    lottieUrl: '',
    background: 'transparent',
    speed: 1,
  },
  propsSchema: {
    speed:      { type: 'number', label: 'Velocidad', min: 0.25, max: 3, step: 0.25, decimals: 2 },
    background: { type: 'color',  label: 'Fondo', allowTransparent: true },
    // `lottieUrl` se setea al elegir desde el buscador — no lo
    // exponemos como input de texto en el formulario.
  },
  Component,
};
